import { useCallback } from 'react';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Typography } from '@mui/material';
import AIBasePopover from 'components/AIBasePopover';
import AIFindingBody from 'components/AIBasePopover/AIFindingBody';
import CitationBody from 'components/AIBasePopover/CitationBody';
import { AISecurityFindingPopoverProps } from './AISecurityFindingPopover.props';
import { settledStatusSx } from './AISecurityFindingPopover.styles';
import GradientDivider from '../../../../Documents/components/SuggestedDocumentReference/GradientDivider';

const AISecurityFindingPopover = ({
  fieldMetadata,
  accuracyConfidence,
  fieldLabel,
  status,
  onAccept,
  onRemove,
  openOnClick = false,
  pendingCitation,
  onAcceptCitation,
  pendingCitationDocumentName,
  children,
}: AISecurityFindingPopoverProps) => {
  const overallConfidence = accuracyConfidence ? parseFloat(accuracyConfidence) : null;
  const { commentary, evidence } = fieldMetadata;
  // A settled finding shows its state (same label + icon as the panel's Reviewed
  // card) and never offers Accept/Dismiss again - there is no un-settle flow, and
  // active buttons here would contradict the panel (SCAL-8969).
  const settled = status === 'accepted' || status === 'dismissed';

  const pendingConfidence = pendingCitation?.tokenReferences?.[0]?.confidence;
  const plainTexts = evidence
    ? [evidence.quote]
    : (pendingCitation?.tokenReferences?.map(t => t.plain_text).filter(Boolean) ?? []);

  const handleAccept = useCallback(() => {
    onAccept?.();
    if (pendingCitation && onAcceptCitation) {
      onAcceptCitation();
    }
  }, [onAccept, onAcceptCitation, pendingCitation]);

  const expandedBody = (
    <>
      <AIFindingBody
        confidence={fieldMetadata.confidence}
        overallConfidence={overallConfidence}
        commentary={commentary}
      />
      {(evidence || pendingCitation) && (
        <>
          <GradientDivider />
          <CitationBody
            sectionLabel="Evidence"
            confidence={pendingConfidence}
            plainTexts={plainTexts}
            documentName={evidence?.file_name ?? pendingCitationDocumentName}
          />
        </>
      )}
      {settled && (
        <>
          <GradientDivider />
          <Typography component="span" sx={settledStatusSx(status === 'accepted')}>
            {status === 'accepted' ? <CheckCircleOutline fontSize="small" /> : <HighlightOffIcon fontSize="small" />}
            {status === 'accepted' ? 'Accepted' : 'Dismissed'}
          </Typography>
        </>
      )}
    </>
  );

  return (
    <AIBasePopover
      title="AI Finding"
      subtitle={fieldLabel}
      acceptLabel="Accept"
      declineLabel="Dismiss"
      onAccept={!settled && (onAccept || onRemove) ? handleAccept : undefined}
      onDecline={!settled ? (onRemove ?? undefined) : undefined}
      expandedBody={expandedBody}
      trigger="click">
      {({ onClick }) => children({ onClick })}
    </AIBasePopover>
  );
};

export default AISecurityFindingPopover;
